import type { Reason } from "./reasons";

export interface Task {
  specimen_id: string;
  kind: string;
  reasons: Reason[];
  due_at: string;
  days_since: number | null;
  interval_days: number;
  product?: string;
  product_id?: string;
  dose?: string;
  /**
   * What the app last learned about this pot's soil. Carried on every task,
   * including a feeding, because a row that is not asking for a Check still
   * owes a reason for the silence — and that row has no Check to hang it off.
   */
  last_soil?: LastSoil;
}

export interface LastSoil {
  /** "check" or "water". A watering carries no reading: nobody felt it. */
  kind: "check" | "water";
  reading?: "wet" | "moist" | "dry" | "very_dry";
  days_ago: number;
}

/** A feeding the app is deliberately not asking for, and the rule that stopped it. */
export interface Blocked {
  specimen_id: string;
  product: string;
  product_id: string;
  dose?: string;
  status: "blocked" | "deferred" | "water_first";
  reason: Reason;
  until: string | null;
}

export interface TaskGroup {
  location_id: string;
  name_ar: string;
  name_en: string;
  placement: "indoor" | "outdoor";
  tasks: Task[];
}

/** A place the guide can actually schedule a Species in. */
export interface SuggestedLocation {
  id: string;
  name_ar: string;
  name_en: string;
  placement: "indoor" | "outdoor";
}

/**
 * A plant somewhere its Species has no interval for. Not an error: the guide
 * genuinely has nothing to say, and saying so is the most useful thing the app
 * can do. `suggested` is somewhere it could go instead, when one exists.
 */
export interface Warning {
  specimen_id: string;
  reason: Reason;
  suggested?: SuggestedLocation;
  /**
   * The owner has already said he is keeping it here. Today leaves it alone,
   * but the collection still flags it — an unschedulable plant that is merely
   * absent is indistinguishable from one that is fine.
   */
  accepted: boolean;
}

export interface Plan {
  at: string;
  locations: TaskGroup[];
  warnings: Warning[];
  blocked: Blocked[];
}

export async function fetchPlan(signal?: AbortSignal): Promise<Plan> {
  const response = await fetch("/v1/tasks", { signal });
  if (!response.ok) throw new Error(`The API answered ${response.status}.`);
  return response.json();
}
